/**
 * ResetPasswordPage: where the recovery email link actually lands.
 *
 * This is the other half of the auth fix described in LoginPage.tsx's
 * header comment. Supabase's client (see supabaseClient.ts's
 * detectSessionInUrl, explicit there for exactly this reason) parses the
 * recovery token out of the URL automatically on load and exchanges it
 * for a real session, firing an onAuthStateChange event with type
 * PASSWORD_RECOVERY — but having a valid recovery session and having a
 * UI that lets you DO anything with it are two different things. Before
 * this page existed, a coordinator could click a reset link, land on a
 * route with no form on it, and have no way to actually set a new
 * password — which is exactly what "the link doesn't work" looks like
 * from the outside, even though the token itself was valid.
 *
 * This page deliberately does NOT require knowing the old password
 * (that's the whole point of a recovery flow) — it relies entirely on
 * the recovery session Supabase's client already established from the
 * URL token. If no such session exists (the link was already used, has
 * expired, or the page was opened directly with no token), it says so
 * clearly rather than showing a form that will just fail.
 */

import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<'checking' | 'ready' | 'no_session'>('checking');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    // By the time this effect runs, the Supabase client has already had
    // a chance to parse the recovery token from the URL (it does this
    // synchronously during client construction / on the initial
    // getSession call) and exchange it for a session. We just need to
    // check whether that session actually exists — if the link was
    // expired or already used, there won't be one, and no form should
    // be shown pretending otherwise.
    let isMounted = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!isMounted) return;
      setStatus(data.session ? 'ready' : 'no_session');
    });

    // Also listen for the PASSWORD_RECOVERY event directly — in some
    // cases the token exchange completes slightly after this page's
    // first render rather than before it, and the listener catches that
    // case without requiring a page reload.
    const { data: subscription } = supabase.auth.onAuthStateChange((event, session) => {
      if (!isMounted) return;
      if (event === 'PASSWORD_RECOVERY' && session) {
        setStatus('ready');
      }
    });

    return () => {
      isMounted = false;
      subscription.subscription.unsubscribe();
    };
  }, []);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError('Please use at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Those two passwords don\u2019t match.');
      return;
    }

    setSubmitting(true);
    const { error: updateError } = await supabase.auth.updateUser({ password });
    setSubmitting(false);

    if (updateError) {
      setError('Something went wrong setting your new password. Please request a new reset link and try again.');
      return;
    }

    setDone(true);
  }

  if (status === 'checking') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-evergreen-700">
        <p className="font-body text-ivory-200">{'Checking your reset link\u2026'}</p>
      </div>
    );
  }

  if (status === 'no_session') {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-evergreen-700 px-6 text-center">
        <p className="font-body text-xs uppercase tracking-[0.2em] text-gold-300">
          The Venue at Lilly Lou&rsquo;s
        </p>
        <h1 className="font-display text-2xl text-ivory-50">This reset link isn&rsquo;t valid</h1>
        <p className="max-w-sm font-body text-sm text-ivory-200">
          It may have already been used, or it may have expired. Reset links are only good for a
          little while and only work once — please request a new one.
        </p>
        <button
          onClick={() => navigate('/login', { replace: true })}
          className="mt-2 rounded bg-gold-400 px-4 py-2 font-body text-sm font-medium text-ink-700 hover:bg-gold-300"
        >
          Back to sign in
        </button>
      </div>
    );
  }

  if (done) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-evergreen-700 px-6 text-center">
        <p className="font-body text-xs uppercase tracking-[0.2em] text-gold-300">
          The Venue at Lilly Lou&rsquo;s
        </p>
        <h1 className="font-display text-2xl text-ivory-50">Your password is set</h1>
        <p className="font-body text-sm text-ivory-200">You&rsquo;re signed in and ready to go.</p>
        <button
          onClick={() => navigate('/', { replace: true })}
          className="mt-2 rounded bg-gold-400 px-4 py-2 font-body text-sm font-medium text-ink-700 hover:bg-gold-300"
        >
          Continue to your weddings
        </button>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-evergreen-700 px-6">
      <div className="w-full max-w-md">
        <div className="mb-10 text-center">
          <p className="font-body text-xs uppercase tracking-[0.2em] text-gold-300">
            The Venue at Lilly Lou&rsquo;s
          </p>
          <h1 className="mt-2 font-display text-4xl font-medium text-ivory-50">
            The Pines Planner
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="rounded-xl bg-ivory-50 p-8 shadow-elevated">
          <h2 className="font-display text-xl text-ink-700">Set a new password</h2>
          <p className="mt-1 font-body text-sm text-ink-400">Use at least 8 characters.</p>

          <div className="mt-6 space-y-4">
            <div>
              <label htmlFor="new-password" className="font-body text-sm font-medium text-ink-600">
                New password
              </label>
              <input
                id="new-password"
                type="password"
                autoComplete="new-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full rounded border border-ivory-300 bg-white px-3 py-2 font-body text-ink-700 outline-none focus:border-gold-400 focus:ring-2 focus:ring-gold-200"
              />
            </div>

            <div>
              <label htmlFor="confirm-password" className="font-body text-sm font-medium text-ink-600">
                Confirm new password
              </label>
              <input
                id="confirm-password"
                type="password"
                autoComplete="new-password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="mt-1 w-full rounded border border-ivory-300 bg-white px-3 py-2 font-body text-ink-700 outline-none focus:border-gold-400 focus:ring-2 focus:ring-gold-200"
              />
            </div>
          </div>

          {error && (
            <p role="alert" className="mt-4 font-body text-sm text-danger">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="mt-6 w-full rounded bg-evergreen-600 px-4 py-2.5 font-body font-medium text-ivory-50 transition-colors hover:bg-evergreen-700 disabled:opacity-60"
          >
            {submitting ? 'Saving\u2026' : 'Set new password'}
          </button>
        </form>
      </div>
    </div>
  );
}
